/**
 * Message Bubble Component
 * @implements FS-002.7.7 - Conversation Message Structure
 * @implements FS-002.7.10 - Message Display (PO vs AI PM visually distinguished)
 * @implements US-020 - AI PM Conversation UI
 *
 * Displays a single conversation message with visual distinction
 * between Product Owner and AI PM messages.
 */

import { Box, Typography, Avatar, Paper, Chip, Stack } from '@mui/material';
import {
  Person as PersonIcon,
  SmartToy as AiIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';

/**
 * Message sender type enum
 * @implements FS-002.7.7 - Message sender type
 */
export type MessageSenderType = 'PRODUCT_OWNER' | 'AI_PM';

/**
 * Agent invocation status enum
 * @implements FS-002.7.7 - Agent Invocations
 */
export type AgentInvocationStatus =
  | 'PENDING'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'FAILED';

/**
 * Agent type enum
 * @implements FS-002.7.7 - Agent Invocations
 */
export type AgentType = 'SPEC_WRITER' | 'AGILE_PROJECT_PLANNER' | 'DEVELOPER';

/**
 * Agent invocation record
 * @implements FS-002.7.7 - Agent Invocations
 */
export interface AgentInvocation {
  agentType: AgentType;
  launchedAt: string;
  completedAt: string | null;
  status: AgentInvocationStatus;
}

/**
 * Artifact type enum
 * @implements FS-002.7.7 - Referenced Artifacts
 */
export type ArtifactType = 'SPECIFICATION' | 'MILESTONE' | 'EPIC';

/**
 * Referenced artifact
 * @implements FS-002.7.7 - Referenced Artifacts
 */
export interface ReferencedArtifact {
  type: ArtifactType;
  id: string;
}

/**
 * Props for MessageBubble component
 */
export interface MessageBubbleProps {
  /** Unique message ID */
  id: string;
  /** Message sender type */
  senderType: MessageSenderType;
  /** Message content */
  content: string;
  /** Timestamp when message was created */
  createdAt: string;
  /** Referenced artifacts in the message */
  referencedArtifacts?: ReferencedArtifact[] | null;
  /** Agent invocations associated with this message */
  agentInvocations?: AgentInvocation[] | null;
}

/**
 * Format timestamp for display
 */
const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Get human-readable agent type name
 */
const getAgentTypeName = (agentType: AgentType): string => {
  switch (agentType) {
    case 'SPEC_WRITER':
      return 'Spec Writer';
    case 'AGILE_PROJECT_PLANNER':
      return 'Agile Planner';
    case 'DEVELOPER':
      return 'Developer';
    default:
      return agentType;
  }
};

/**
 * Get status color for agent invocation
 */
const getStatusColor = (
  status: AgentInvocationStatus,
): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
  switch (status) {
    case 'PENDING':
      return 'default';
    case 'IN_PROGRESS':
      return 'primary';
    case 'COMPLETED':
      return 'success';
    case 'FAILED':
      return 'error';
    default:
      return 'default';
  }
};

/**
 * MessageBubble Component
 * @implements FS-002.7.10 - PO and AI PM messages are visually distinguished
 *
 * Renders a message bubble with appropriate styling based on sender type.
 * PO messages appear on the right in primary color.
 * AI PM messages appear on the left in a neutral color.
 *
 * @example
 * ```tsx
 * <MessageBubble
 *   id="msg-1"
 *   senderType="PRODUCT_OWNER"
 *   content="Hello, AI PM!"
 *   createdAt="2026-01-13T10:00:00Z"
 * />
 * ```
 */
export const MessageBubble = ({
  senderType,
  content,
  createdAt,
  referencedArtifacts,
  agentInvocations,
}: MessageBubbleProps) => {
  const isPO = senderType === 'PRODUCT_OWNER';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isPO ? 'row-reverse' : 'row',
        gap: 1.5,
        mb: 2,
        maxWidth: '100%',
      }}
    >
      {/* Avatar */}
      <Avatar
        sx={{
          bgcolor: isPO ? 'primary.main' : 'secondary.main',
          width: 36,
          height: 36,
          flexShrink: 0,
        }}
      >
        {isPO ? <PersonIcon fontSize="small" /> : <AiIcon fontSize="small" />}
      </Avatar>

      {/* Message Content */}
      <Box
        sx={{
          maxWidth: '75%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: isPO ? 'flex-end' : 'flex-start',
        }}
      >
        {/* Sender Label */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 0.5, px: 1 }}
        >
          {isPO ? 'You' : 'AI Project Manager'}
        </Typography>

        {/* Message Bubble */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            backgroundColor: isPO ? 'primary.main' : 'grey.100',
            color: isPO ? 'primary.contrastText' : 'text.primary',
            borderRadius: 2,
            borderTopRightRadius: isPO ? 0 : 2,
            borderTopLeftRadius: isPO ? 2 : 0,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {content}
          </Typography>

          {/* Referenced Artifacts */}
          {referencedArtifacts && referencedArtifacts.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1 }}>
              {referencedArtifacts.map((artifact, index) => (
                <Chip
                  key={`${artifact.type}-${artifact.id}-${index}`}
                  label={`${artifact.type.toLowerCase()}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.7rem',
                    height: 20,
                    borderColor: isPO
                      ? 'rgba(255,255,255,0.5)'
                      : 'primary.main',
                    color: isPO ? 'inherit' : 'primary.main',
                  }}
                />
              ))}
            </Stack>
          )}
        </Paper>

        {/* Agent Invocations (only for AI PM messages) */}
        {!isPO && agentInvocations && agentInvocations.length > 0 && (
          <Stack
            direction="row"
            spacing={0.5}
            flexWrap="wrap"
            sx={{ mt: 0.5, px: 1 }}
          >
            {agentInvocations.map((invocation, index) => (
              <Chip
                key={`${invocation.agentType}-${index}`}
                icon={<ScheduleIcon sx={{ fontSize: '0.75rem !important' }} />}
                label={getAgentTypeName(invocation.agentType)}
                size="small"
                color={getStatusColor(invocation.status)}
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: 18 }}
              />
            ))}
          </Stack>
        )}

        {/* Timestamp */}
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ mt: 0.5, px: 1 }}
        >
          {formatTimestamp(createdAt)}
        </Typography>
      </Box>
    </Box>
  );
};
